import { Link } from 'react-router-dom';
import { relativeTime } from '../../../utils/dates';

/**
 * One sentence: is anything wrong, whose, how big, how old.
 *
 * It used to read "Critical reputation risk detected - act now", which names no
 * subject and dates nothing. In production every alert belonged to a single
 * account and the newest was 22 days old, so "act now" was both anonymous and
 * misleading.
 */
export function Verdict({ accountsNeedingAttention = [], newestAlertAt, staleCount = 0 }) {
	const count = accountsNeedingAttention.length;

	if (count === 0) {
		return (
			<section className="rounded-2xl border border-border bg-card p-5 shadow-card">
				<div className="flex items-center gap-2.5">
					<span className="h-2.5 w-2.5 rounded-full bg-green-600" />
					<h2 className="text-lg font-semibold text-foreground">Nothing needs your attention</h2>
				</div>
				<p className="mt-1 text-sm text-muted-foreground">
					{staleCount > 0
						? `No high-risk findings in this window. ${staleCount} account${staleCount === 1 ? '' : 's'} ${staleCount === 1 ? 'has' : 'have'} not been scanned recently, though - see coverage below.`
						: 'No high-risk findings, and every account has been scanned recently.'}
				</p>
			</section>
		);
	}

	const worst = accountsNeedingAttention[0];
	// Coloured by the severity actually present, not by riskBand(worst.risk):
	// alerts carry no account risk score, so that banded null and rendered a
	// grey dot above "2 critical, 6 high".
	const dot = worst.critical > 0 ? 'bg-red-600' : 'bg-orange-500';
	const age = relativeTime(newestAlertAt);
	const totalFindings = accountsNeedingAttention.reduce((n, a) => n + a.total, 0);

	return (
		<section className="rounded-2xl border border-border bg-card p-5 shadow-card">
			<div className="flex flex-wrap items-center gap-2.5">
				<span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
				<h2 className="text-lg font-semibold text-foreground">
					{count === 1 ? 'One account needs attention' : `${count} accounts need attention`}
				</h2>
			</div>
			<p className="mt-1 text-sm text-muted-foreground">
				{count === 1 ? (
					<>
						<Link to="/clients" className="font-medium text-foreground hover:underline">@{worst.handle}</Link>
						{worst.clientName ? ` · ${worst.clientName}` : ''}
						{' · '}
						{describeSeverity(worst)}
						{age ? ` · newest finding ${age}` : ''}
					</>
				) : (
					<>
						{totalFindings} findings across {count} accounts, worst is{' '}
						<span className="font-medium text-foreground">@{worst.handle}</span>
						{' · '}{describeSeverity(worst)}
						{age ? ` · newest ${age}` : ''}
					</>
				)}
			</p>
		</section>
	);
}

function describeSeverity({ critical = 0, high = 0 }) {
	const parts = [];
	if (critical) parts.push(`${critical} critical`);
	if (high) parts.push(`${high} high`);
	return parts.join(', ') || 'findings';
}
